import React, { Component } from 'react';
import {
  shape,
  string,
  func,
  bool
} from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { hasPermission, surveySchemaPermission } from 'unihow-common';
import { withLang } from '../../i18n';
import {
  addErrorMessageAction,
  putSurveySchemaAction,
  setTaskFulfilled,
  setTaskPending
} from '../../actions';
import ActionBar from '../SurveyPartView/ActionBar';
import InfoBox from './SurveyInfoBox';
import SurveySchemaPreviewTabs from '../Preview/SchemaPreview/PreviewTabs';
import PreviewLanguageSelector from '../Preview/PreviewLanguageSelector';
import { getSurveySchema } from '../../api';
import { getCompiledPath, setDocumentTitle } from '../../util';
import { routes } from '../../constants';

import styles from './surveySchemaView.module.css';
import { publicationStatusValues } from '../../constants/types';
import withRouter from '../WithRouter';
import { withPreviousLocation } from '../PreviousLocationProvider';

class SurveySchemaView extends Component {
  constructor(props) {
    super(props);

    this.onArchive = this.onArchive.bind(this);
    this.onLanguageSelect = this.onLanguageSelect.bind(this);

    const { previousLocation } = props;
    const entryReferer =
      previousLocation && previousLocation.pathname.startsWith(routes.surveySchemas.surveyParts.index)
        ? previousLocation.pathname
        : routes.surveySchemas.schemas.index;

    this.state = {
      surveySchemaId: Number(props.params.surveySchemaId),
      previewLang: props.lang,
      entryReferer
    };
  }

  componentDidMount() {
    const
      { dispatchSetTaskPending, dispatchSetTaskFulfilled, dispatchAddErrorMessage }
        = this.props;
    const { surveySchemaId } = this.state;
    dispatchSetTaskPending();
    getSurveySchema(surveySchemaId)
      .then((surveySchema) => this.setState({ surveySchema }))
      .catch(dispatchAddErrorMessage)
      .finally(dispatchSetTaskFulfilled);
  }

  onArchive() {
    const schema = this.state.surveySchema;
    this.props.dispatchPutSurveySchema(schema.id, { status: publicationStatusValues.ARCHIVED })
      .then((result) => this.setState({ surveySchema: result.value }));
  }

  onLanguageSelect(previewLang) {
    this.setState({ previewLang });
  }

  render() {
    const {
      allowedToEdit,
      navigate,
      translate: t,
      lang
    } = this.props;
    const {
      surveySchemaId, surveySchema, previewLang, entryReferer
    } = this.state;

    const navigateTo = (route) => () => navigate(getCompiledPath(route, { surveySchemaId }));

    if (!surveySchema) {
      return null;
    }
    const isPublished = surveySchema.status === publicationStatusValues.PUBLISHED;
    const isArchived = surveySchema.status === publicationStatusValues.ARCHIVED;
    setDocumentTitle(t('surveySchemas.schema'));

    return (
      <div className="page-container">
        <div className={styles.titleRow}>
          <h1 className={`page-title--small ${styles.title}`}>
            {t('surveySchemas.schema')}
          </h1>
          {allowedToEdit && (
            <ActionBar
              onEdit={!isArchived ? navigateTo(routes.surveySchemas.schemas.schema.edit.index) : undefined}
              onNewVersion={isPublished ? navigateTo(routes.surveySchemas.schemas.schema.newVersion.index) : undefined}
              onCopy={navigateTo(routes.surveySchemas.schemas.schema.copy.index)}
              onArchive={!isArchived ? this.onArchive : undefined}
              archiveTranslationKey="actions.confirmArchiveSurveySchema"
            />
          )}
        </div>
        <PreviewLanguageSelector
          previewLang={previewLang}
          onLanguageSelect={this.onLanguageSelect}
        />
        <InfoBox
          surveySchema={surveySchema}
          translate={t}
          lang={lang}
        />
        <SurveySchemaPreviewTabs
          surveySchema={surveySchema}
          schemaParts={surveySchema.surveySchemaParts}
          extraSurveyPart={surveySchema.extraSurveyPart}
          translate={t}
          previewLang={previewLang}
        />
        <div className={styles.navigation}>
          <Link className="button--action" to={entryReferer}>
            {t('common.back')}
          </Link>
        </div>
      </div>

    );
  }
}

SurveySchemaView.propTypes = {
  allowedToEdit: bool.isRequired,
  previousLocation: shape({
    pathname: string
  }).isRequired,
  params: shape({
    surveyId: string
  }).isRequired,
  translate: func.isRequired,
  lang: string.isRequired,
  dispatchAddErrorMessage: func.isRequired,
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  dispatchPutSurveySchema: func.isRequired,
  navigate: func.isRequired
};

const mapStateToProps = ({ user }) => ({
  allowedToEdit: hasPermission(user.currentUser.permissions, surveySchemaPermission(true))
});

const mapDispatchToProps = (dispatch) => ({
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled()),
  dispatchPutSurveySchema:
    (id, surveySchemaOptions) => dispatch(putSurveySchemaAction(id, surveySchemaOptions))
});

export default withLang(
  withRouter(withPreviousLocation(connect(mapStateToProps, mapDispatchToProps)(SurveySchemaView)))
);
